import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faApple } from '@fortawesome/free-brands-svg-icons';
import type { ApplePayButtonType } from '@stripe/stripe-js';
import { useState } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage, useIntl } from 'react-intl';
import styled, { css } from 'styled-components';

import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { useMutation } from '#src/hooks/graphql/useMutation.ts';
import { useFiatBalance } from '#src/hooks/wallets/useFiatBalance.ts';
import { glossary } from '#src/lib/glossary.ts';
import type { MonetaryAmount } from '#src/lib/monetaryAmount.ts';

import type {
  ValidateApplePayMerchantSession,
  ValidateApplePayMerchantSessionVariables,
} from './__generated__/index.graphql.ts';

const VALIDATE_APPLE_PAY_MERCHANT_SESSION_MUTATION = gql`
  mutation ValidateApplePayMerchantSession(
    $input: validateApplePayMerchantSessionInput!
  ) {
    validateApplePayMerchantSession(input: $input) {
      applePaySessionData
      errors {
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  ValidateApplePayMerchantSession,
  ValidateApplePayMerchantSessionVariables
>;

// https://developer.apple.com/documentation/apple_pay_on_the_web/displaying_apple_pay_buttons_using_css
const Button = styled.div<{ label?: ApplePayButtonType }>`
  @supports (-webkit-appearance: -apple-pay-button) {
    display: inline-block;
    /* stylelint-disable-next-line property-no-vendor-prefix */
    -webkit-appearance: -apple-pay-button;
    ${({ label }) => css`
      -apple-pay-button-type: ${label || 'buy'};
    `}
    height: 40px;
    border-radius: var(--unit);
    cursor: pointer;
    > * {
      display: none;
    }
    -apple-pay-button-style: white;
  }

  @supports not (-webkit-appearance: -apple-pay-button) {
    --apple-pay-scale: 1; /* (height / 32) */
    cursor: pointer;
    justify-content: center;
    padding: 0px;
    box-sizing: border-box;
    min-width: 200px;
    min-height: 40px;
    border-radius: var(--unit);
    max-height: 64px;
    background-color: white;
    color: black;
    display: flex;
    gap: var(--unit);
    align-items: center;
    font-weight: bold;
  }
`;

export type Props = {
  monetaryAmount: MonetaryAmount;
  handleToken: (
    token: ApplePayJS.ApplePayPaymentToken
  ) => Promise<{ errors?: string[] }>;
  onSuccess?: () => void;
  label?: ApplePayButtonType;
  labelMessage?: MessageDescriptor;
};

export const ApplePayButton = ({
  monetaryAmount,
  handleToken,
  onSuccess,
  label,
  labelMessage = glossary.buy,
}: Props) => {
  const { locale } = useIntl();
  const { fiatCurrency } = useFiatBalance();
  const [validate] = useMutation(VALIDATE_APPLE_PAY_MERCHANT_SESSION_MUTATION, {
    showErrorsWithSnackNotification: true,
  });
  const [enabled, setEnabled] = useState(true);

  const onClick = () => {
    if (!enabled) return;
    setEnabled(false);
    const session = new ApplePaySession(3, {
      countryCode: 'FR',
      currencyCode: fiatCurrency.toUpperCase(),
      merchantCapabilities: ['supports3DS'],
      supportedNetworks: ['masterCard', 'visa'],
      total: {
        label: 'Total',
        amount: monetaryAmount.toMajorUnit(fiatCurrency).toString(),
      },
    });
    session.onvalidatemerchant = async event => {
      const results = await validate({
        variables: {
          input: {
            merchantValidationUrl: event.validationURL,
          },
        },
      });
      if (results.data?.validateApplePayMerchantSession?.applePaySessionData) {
        session.completeMerchantValidation(
          JSON.parse(
            results.data.validateApplePayMerchantSession.applePaySessionData
          )
        );
      }
    };
    session.onpaymentauthorized = async event => {
      const result = await handleToken(event.payment.token);
      if (!result.errors?.length) {
        session.completePayment(ApplePaySession.STATUS_SUCCESS);
        onSuccess?.();
        return;
      }
      session.completePayment(ApplePaySession.STATUS_FAILURE);
    };
    session.oncancel = () => {
      setEnabled(true);
    };
    session.begin();
  };
  return (
    <Button label={label} lang={locale} onClick={onClick}>
      <FontAwesomeIcon icon={faApple} />
      <FormattedMessage {...labelMessage} />
    </Button>
  );
};
